import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, TrayArrowDown, Warehouse, BellRinging } from "@phosphor-icons/react";
import { money, Spinner } from "../components/stock/stockUtils";
import { PartsTab } from "../components/stock/PartsTab";
import { LocationsTab } from "../components/stock/LocationsTab";
import { ReorderTab } from "../components/stock/ReorderTab";
import { PartsPosTab } from "../components/stock/PartsPosTab";
import { MovementsTab } from "../components/stock/MovementsTab";
import { MovementDialog } from "../components/stock/MovementDialog";

const WRITE_ROLES = ["super_admin", "admin", "project_manager"];
const TABS = ["parts", "locations", "reorder", "pos", "movements"];

export default function StockPage() {
  const { user } = useAuth();
  const canEdit = WRITE_ROLES.includes(user?.role);
  const [sp, setSp] = useSearchParams();
  const tab = TABS.includes(sp.get("tab")) ? sp.get("tab") : "parts";
  const setTab = (t) => setSp((p) => { const n = new URLSearchParams(p); n.set("tab", t); n.delete("po"); return n; });
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState(null);
  const [tick, setTick] = useState(0);
  const [receive, setReceive] = useState(false);
  const [parts, setParts] = useState([]);
  const [openAdd, setOpenAdd] = useState("");
  const [openPo, setOpenPo] = useState(sp.get("po") || "");

  const refresh = useCallback(() => {
    API.get("/stock/summary").then((r) => setSummary(r.data)).catch(() => setSummary({}));
    API.get("/stock/options").then((r) => setOptions(r.data)).catch(() => setOptions({ suppliers: [], operatives: [], locations: [], categories: [], units: ["each"] }));
    setTick((t) => t + 1);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (receive) API.get("/stock/parts").then((r) => setParts(r.data.parts)).catch(() => {}); }, [receive]);

  const s = summary || {};
  const attention = (s.low || 0) + (s.out || 0);
  return (
    <div className="space-y-4" data-testid="stock-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold font-[Chivo]">Stock &amp; parts</h1>
          <p className="text-sm text-muted-foreground">Van and store stock, reorder points and supplier parts orders — every spare fitted lands on its job as cost.</p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => { setTab("locations"); setOpenAdd("location"); }} data-testid="stock-add-location-btn"><Warehouse size={16} className="mr-1" /> New location</Button>
            <Button variant="outline" onClick={() => setReceive(true)} disabled={!options?.locations?.length} data-testid="stock-receive-btn"><TrayArrowDown size={16} className="mr-1" /> Receive stock</Button>
            <Button onClick={() => { setTab("parts"); setOpenAdd("part"); }} data-testid="stock-add-part-btn"><Plus size={16} className="mr-1" /> Add part</Button>
          </div>
        )}
      </div>

      {summary === null ? <Spinner /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stock-stats">
          <Stat label="Parts" value={s.parts ?? 0} sub={`${s.locations ?? 0} location${s.locations === 1 ? "" : "s"}`} onClick={() => setTab("parts")} testid="stock-stat-parts" />
          <Stat label="Stock value" value={money(s.stock_value)} testid="stock-stat-value" />
          <Stat label="Needs reorder" value={attention} sub={s.out ? `${s.out} out of stock` : undefined} color={s.out ? "text-red-600" : attention ? "text-amber-600" : ""} onClick={() => setTab("reorder")} testid="stock-stat-reorder" />
          <Stat label="On order" value={s.on_order ?? 0} sub={`${s.open_pos ?? 0} open PO${s.open_pos === 1 ? "" : "s"}`} color={s.on_order ? "text-blue-600" : ""} onClick={() => setTab("pos")} testid="stock-stat-on-order" />
          <Stat label="Issued this month" value={money(s.issued_this_month)} sub="parts cost on jobs" onClick={() => setTab("movements")} testid="stock-stat-issued" />
          <Stat label="Locations" value={s.locations ?? 0} onClick={() => setTab("locations")} testid="stock-stat-locations" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="parts" data-testid="stock-tab-parts">Parts</TabsTrigger>
          <TabsTrigger value="locations" data-testid="stock-tab-locations">Stores &amp; vans</TabsTrigger>
          <TabsTrigger value="reorder" data-testid="stock-tab-reorder"><BellRinging size={14} className="mr-1" /> Reorder{attention > 0 && <span className="ml-1.5 rounded-full bg-amber-500 text-white text-[10px] px-1.5" data-testid="stock-reorder-badge">{attention}</span>}</TabsTrigger>
          <TabsTrigger value="pos" data-testid="stock-tab-pos">Purchase orders</TabsTrigger>
          <TabsTrigger value="movements" data-testid="stock-tab-movements">Movements</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "parts" && <PartsTab options={options} refreshKey={tick} onChanged={refresh} canEdit={canEdit} openAdd={openAdd === "part"} onAddHandled={() => setOpenAdd("")} />}
      {tab === "locations" && <LocationsTab options={options} refreshKey={tick} onChanged={refresh} canEdit={canEdit} openAdd={openAdd === "location"} onAddHandled={() => setOpenAdd("")} />}
      {tab === "reorder" && <ReorderTab options={options} refreshKey={tick} canEdit={canEdit} onPoCreated={(po) => { refresh(); setOpenPo(po.id); setTab("pos"); }} />}
      {tab === "pos" && <PartsPosTab options={options} refreshKey={tick} onChanged={refresh} canEdit={canEdit} openPoId={openPo} onPoOpened={() => setOpenPo("")} />}
      {tab === "movements" && <MovementsTab refreshKey={tick} />}

      <MovementDialog open={receive} initialType="receipt" parts={parts} locations={options?.locations || []} onClose={() => setReceive(false)} onDone={refresh} />
    </div>
  );
}

function Stat({ label, value, sub, color = "", onClick, testid }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} className={`border border-border rounded-lg p-3 bg-card text-left ${onClick ? "hover:bg-muted/40 transition-colors" : ""}`} data-testid={testid}>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-bold font-[Chivo] mt-0.5 ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </Tag>
  );
}
